import { devtoolsExchange } from '@urql/devtools';
import { cacheExchange, offlineExchange } from '@urql/exchange-graphcache';
import { retryExchange } from '@urql/exchange-retry';
import config from 'config';
import { createClient, fetchExchange, subscriptionExchange } from 'urql';

import Platform from '@lyearn/core/utils/Platform';
import productivitySchema from '@/types/productivity-schema-json.json';
import schema from '@/types/schema-json.json';

import cacheKeys from './cache/keys';
import resolvers from './cache/resolvers';
import authExchange from './authExchange';
import errorExchange from './errorExchange';
import { filterUnAuthExchange } from './filterUnAuthExchange';
import interactionManagerExchange from './interactionManager';
import { refocusExchange } from './refocusExchange';
import { requestPolicyExchange } from './requestPolicyExchange';
import getStorage from './storage';
import { createSubscriptionClient } from './subscriptionClient';

const cacheMutations = (
  typeof window !== 'undefined' ? require('./cache/mutations').default : {}
) as typeof import('./cache/mutations').default;
const optimistic = (
  typeof window !== 'undefined' ? require('./cache/optimistic').default : {}
) as typeof import('./cache/optimistic').default;
const cacheSubscription = (
  typeof window !== 'undefined' ? require('./cache/subscription').default : {}
) as typeof import('./cache/subscription').default;

const NO_CONTENT_MESSAGE = '[Network] No Content';

type GetClientType = {
  enableURQLOfflineCache?: boolean;
};

type GetExchangesType = {
  ssrExchange?: typeof fetchExchange;
  enableURQLOfflineCache?: boolean;
  graphcacheSchema: any;
  graphcacheResolvers?: any;
  graphcacheKeys?: any;
  graphcacheUpdates?: any;
  graphcacheOptimistic?: any;
  withSubscription?: boolean;
};

const createGraphcacheConfig = ({
  graphcacheSchema,
  graphcacheResolvers,
  graphcacheKeys,
  graphcacheUpdates,
  graphcacheOptimistic,
}: Omit<GetExchangesType, 'ssrExchange' | 'withSubscription' | 'enableURQLOfflineCache'>) => ({
  resolvers: graphcacheResolvers,
  // @ts-ignore
  keys: graphcacheKeys,
  updates: graphcacheUpdates,
  // @ts-ignore
  schema: graphcacheSchema,
  optimistic: graphcacheOptimistic,
});

export const getExchanges = (
  ssrExchange?: typeof fetchExchange,
  enableURQLOfflineCache?: boolean,
) => {
  return getConfiguredExchanges({
    ssrExchange,
    enableURQLOfflineCache,
    graphcacheSchema: schema,
    graphcacheResolvers: resolvers,
    graphcacheKeys: cacheKeys,
    graphcacheUpdates: { Mutation: cacheMutations, Subscription: cacheSubscription },
    graphcacheOptimistic: optimistic,
    withSubscription: true,
  });
};

export const getProductivityExchanges = (
  ssrExchange?: typeof fetchExchange,
  enableURQLOfflineCache?: boolean,
) => {
  return getConfiguredExchanges({
    ssrExchange,
    enableURQLOfflineCache,
    graphcacheSchema: productivitySchema,
    withSubscription: false,
  });
};

const getConfiguredExchanges = ({
  ssrExchange,
  enableURQLOfflineCache,
  graphcacheSchema,
  graphcacheResolvers,
  graphcacheKeys,
  graphcacheUpdates,
  graphcacheOptimistic,
  withSubscription,
}: GetExchangesType) => {
  const subscriptionClient = typeof window !== 'undefined' ? createSubscriptionClient() : null;
  const graphcacheConfig = createGraphcacheConfig({
    graphcacheSchema,
    graphcacheResolvers,
    graphcacheKeys,
    graphcacheUpdates,
    graphcacheOptimistic,
  });
  const storage = getStorage();
  const normalizedCacheExchange =
    enableURQLOfflineCache && storage
      ? offlineExchange({
          ...graphcacheConfig,
          storage,
        })
      : cacheExchange(graphcacheConfig);

  return [
    devtoolsExchange,
    refocusExchange(),
    requestPolicyExchange(),
    normalizedCacheExchange,
    filterUnAuthExchange,
    retryExchange({
      retryIf: (err) => !!(err.message === NO_CONTENT_MESSAGE || err.networkError),
    }),
    Platform.select({ native: interactionManagerExchange }),
    errorExchange(),
    authExchange(),
    ssrExchange,
    fetchExchange,
    withSubscription && subscriptionClient
      ? subscriptionExchange({
          forwardSubscription: (operation) => ({
            subscribe: (sink) => {
              // @ts-ignore
              const dispose = subscriptionClient.subscribe(operation, sink);

              return {
                unsubscribe: dispose,
              };
            },
          }),
        })
      : undefined,
  ].filter(Boolean);
};

const getClient = ({ enableURQLOfflineCache }: GetClientType) => {
  return createClient({
    url: `${config.origin}/api/graphql`,
    exchanges: getExchanges(undefined, enableURQLOfflineCache),
  });
};

export const getProductivityClient = ({ enableURQLOfflineCache }: GetClientType = {}) => {
  return createClient({
    url: `${config.origin}/api/productivity/graphql`,
    exchanges: getProductivityExchanges(undefined, enableURQLOfflineCache),
  });
};

export default getClient;
